import { createReducer, on } from '@ngrx/store';
import {
  SubmittalPackageListingItem,
  SubmittalPackageFilterParams,
  SubmittalRegistry,
  SubmittalRegistryFilterParams,
  SubmittalPackageResp,
} from './submittals.types';
import { submittalsActions } from './submittals.actions';
import { viewProjectActions } from '@app/store/view-project/view-project.actions';
import { submittalEntrySidebarActions } from '@app/framework/commitments/sidebar/submittal-entry-sidebar/submittal-entry-sidebar.actions';
import { submittalRegistryListingActions } from '@app/pages/webapp/projects/view-project/submittals/submittal-registry-listing/submittal-registry-listing.actions';
import { submittalPackageDetailsActions } from '@app/pages/webapp/projects/view-project/submittals/submittal-package-details-sidebar/submittal-package-details.actions';

export const submittalsFeatureKey = 'submittals';
export const SUBMITTALS_PER_PAGE = 20;

export interface SubmittalsState {
  // Submittal Packages Log
  packages: SubmittalPackageListingItem[];
  packagesListLoading: boolean;
  packagesListFilters: Partial<SubmittalPackageFilterParams>;
  packagesReachedEnd: boolean;

  // Submittal Package Details
  packageDetails: SubmittalPackageResp;
  packageDetailsLoading: boolean;
  packageRegistryEntriesLoading: Record<number, boolean>;

  // Submittal Registry
  submittalRegistries: SubmittalRegistry[];
  registriesLoading: boolean;
  registriesError: string | null;
  registriesFilters: Partial<SubmittalRegistryFilterParams>;
  registriesReachedEnd: boolean;

  // The ID that is going to be autofilled from backend when trying to create the next registry
  nextRegistryId: string | null;
  nextRegistryIdIsLoading: boolean;
}

export const initialSubmittalsState: SubmittalsState = {
  // Submittal Packages Log
  packages: [],
  packagesListLoading: false,
  packagesListFilters: {},
  packagesReachedEnd: false,

  // Submittal Package Details
  packageDetails: null,
  packageDetailsLoading: false,
  packageRegistryEntriesLoading: {},

  // Submittal Registry
  submittalRegistries: [],
  registriesLoading: false,
  registriesError: null,
  registriesFilters: {},
  registriesReachedEnd: false,

  nextRegistryId: null,
  nextRegistryIdIsLoading: false,
};

export const submittalsReducer = createReducer(
  initialSubmittalsState,

  // Submittal Package Reducers
  on(
    viewProjectActions.refreshNeeded,
    submittalsActions.submittalPackageListOpened,
    (state): SubmittalsState => ({
      ...state,
      packagesListLoading: true,
    }),
  ),

  on(
    submittalsActions.packageListFiltersChanged,
    (state, action): SubmittalsState => ({
      ...state,
      packagesListLoading: true,
      packagesListFilters: {
        ...state.packagesListFilters,
        ...action.filters,
      },
    }),
  ),

  on(submittalsActions.loadPackageListSuccess, (state, action): SubmittalsState => {
    const packagesReachedEnd = action.packages?.length < SUBMITTALS_PER_PAGE;
    const page = action.filters?.page;
    const packages = page === 1 ? action.packages : [...state.packages, ...action.packages];

    return {
      ...state,
      packagesListLoading: false,
      packages,
      packagesReachedEnd,
      packagesListFilters: {
        ...state.packagesListFilters,
        ...action.filters,
      },
    };
  }),

  on(
    submittalsActions.loadPackageListFailure,
    (state, action): SubmittalsState => ({
      ...state,
      packagesListLoading: false,
    }),
  ),

  on(submittalsActions.loadPackageDetailsSuccess, (state, action): SubmittalsState => {
    return {
      ...state,
      packageDetails: action.packageDetails,
    };
  }),

  on(
    submittalPackageDetailsActions.onPackageDetailsClose,
    (state): SubmittalsState => ({
      ...state,
      packageDetailsLoading: false,
      packageDetails: null,
    }),
  ),
  on(submittalsActions.updatePackageRegistryEntry, (state, action): SubmittalsState => {
    const packageRegistryEntryLoading = {
      ...state.packageRegistryEntriesLoading,
      [action.registryEntryId]: true,
    };

    return {
      ...state,
      packageRegistryEntriesLoading: packageRegistryEntryLoading,
    };
  }),
  on(submittalsActions.updatePackageRegistryEntrySuccess, (state, action): SubmittalsState => {
    const packageDetails = {
      ...state.packageDetails,
      entries: state.packageDetails.entries.map((entry) =>
        entry.id === action.registryEntry.id ? action.registryEntry : entry,
      ),
    };

    const packageRegistryEntryLoading = {
      ...state.packageRegistryEntriesLoading,
      [action.registryEntry.id]: false,
    };

    return {
      ...state,
      packageDetails,
      packageRegistryEntriesLoading: packageRegistryEntryLoading,
    };
  }),
  on(submittalsActions.updatePackageRegistryEntryFailure, (state, action): SubmittalsState => {
    const packageRegistryEntryLoading = {
      ...state.packageRegistryEntriesLoading,
      [action.registryEntryId]: false,
    };

    return {
      ...state,
      packageRegistryEntriesLoading: packageRegistryEntryLoading,
    };
  }),

  on(
    submittalsActions.loadPackageDetails,
    submittalsActions.updateSubmittalPackage,
    submittalsActions.updateSubmittalPackageStatus,
    submittalPackageDetailsActions.voidPackage,
    submittalPackageDetailsActions.closePackage,
    submittalPackageDetailsActions.reopenPackage,
    submittalPackageDetailsActions.sendPackageForApproval,
    submittalPackageDetailsActions.markAsComplete,
    submittalPackageDetailsActions.markAsReady,
    submittalPackageDetailsActions.submitToOwner,
    submittalPackageDetailsActions.createRevision,
    submittalPackageDetailsActions.deletePackage,
    (state): SubmittalsState => ({
      ...state,
      packageDetailsLoading: true,
    }),
  ),

  on(
    submittalsActions.loadPackageDetailsSuccess,
    submittalsActions.loadPackageDetailsFailure,
    submittalsActions.updateSubmittalPackageSuccess,
    submittalsActions.updateSubmittalPackageFailure,
    submittalsActions.updateSubmittalPackageStatusSuccess,
    submittalsActions.updateSubmittalPackageStatusFailure,
    submittalsActions.voidPackageSuccess,
    submittalsActions.voidPackageFailure,
    submittalsActions.closePackageSuccess,
    submittalsActions.closePackageFailure,
    submittalsActions.reopenPackageSuccess,
    submittalsActions.reopenPackageFailure,
    submittalsActions.sendPackageForApprovalSuccess,
    submittalsActions.sendPackageForApprovalFailure,
    submittalsActions.submitPackageToOwnerSuccess,
    submittalsActions.submitPackageToOwnerFailure,
    submittalsActions.createRevisionForPackageSuccess,
    submittalsActions.createRevisionForPackageFailure,
    submittalsActions.deletePackageSuccess,
    submittalsActions.deletePackageFailure,

    (state, action): SubmittalsState => {
      return {
        ...state,
        packageDetailsLoading: false,
      };
    },
  ),

  on(
    submittalsActions.updateSubmittalPackageSuccess,
    submittalsActions.updateSubmittalPackageStatusSuccess,
    submittalsActions.submitPackageToOwnerSuccess,
    submittalsActions.cancelSubmitPackageToOwnerSuccess,
    (state, action): SubmittalsState => {
      return {
        ...state,
        packageDetails: {
          ...state.packageDetails,
          ...action.submittalPackage,
        },
      };
    },
  ),

  // Submittal Registry Reducers
  on(
    submittalsActions.loadSubmittalRegistryItems,
    (state): SubmittalsState => ({
      ...state,
      registriesLoading: true,
      registriesError: null,
    }),
  ),

  on(
    submittalRegistryListingActions.submittalRegistryFiltersChanged,
    (state, action): SubmittalsState => ({
      ...state,
      registriesLoading: true,
      registriesError: null,
      registriesFilters: {
        ...state.registriesFilters,
        ...action.filters,
      },
    }),
  ),

  on(submittalsActions.loadSubmittalRegistriesSuccess, (state, action): SubmittalsState => {
    const registryReachedEnd = action.registries?.length !== SUBMITTALS_PER_PAGE;
    const page = action.filters?.page;
    const submittalRegistries =
      page === 1 ? action.registries : [...state.submittalRegistries, ...action.registries];

    return {
      ...state,
      registriesLoading: false,
      submittalRegistries,
      registriesError: null,
      registriesReachedEnd: registryReachedEnd,
      registriesFilters: {
        ...state.registriesFilters,
        ...action.filters,
      },
    };
  }),

  on(
    submittalsActions.loadSubmittalRegistriesFailure,
    (state, action): SubmittalsState => ({
      ...state,
      registriesLoading: false,
      registriesError: action.error,
    }),
  ),

  on(
    submittalEntrySidebarActions.loadPage,
    (state): SubmittalsState => ({
      ...state,
      nextRegistryIdIsLoading: true,
    }),
  ),

  on(
    submittalsActions.loadNextRegistryIdSuccess,
    (state, action): SubmittalsState => ({
      ...state,
      nextRegistryId: action.nextRegistryId,
      nextRegistryIdIsLoading: false,
    }),
  ),
  on(
    submittalsActions.loadNextRegistryIdFailure,
    (state): SubmittalsState => ({
      ...state,
      nextRegistryId: null,
      nextRegistryIdIsLoading: false,
    }),
  ),
);
